import { useEffect, useState } from 'react';
import { getOrder, type OrderSummary } from '../api/orders';

const POLL_MS = 2000;
const MAX_POLLS = 10;

/** `settling` while the webhook is still expected, `unconfirmed` once we stopped waiting. */
export type SettlementState = 'settling' | 'unconfirmed' | null;

/**
 * `paid` is not the only status the money has arrived under: `merged`, `shipped` and
 * `pickup` are all reached from `paid` and never from an unpaid order. `paidAt` is the
 * one field they share, so read that instead of enumerating the statuses. Cancellation
 * leaves `paidAt` set — the refund happens out-of-band — so it has to be excluded or a
 * cancelled order would thank the buyer for a payment they no longer hold.
 */
export function isPaymentConfirmed(order: OrderSummary | null): boolean {
  if (!order?.paidAt) return false;
  return order.status !== 'cancelled';
}

type Args = {
  orderId: string | undefined;
  token: string | null;
  order: OrderSummary | null;
  onOrder: (order: OrderSummary) => void;
  /** The buyer came back from a Stripe session that completed, so the money is gone. */
  returnedFromPayment: boolean;
};

/**
 * Stripe hands the buyer back the instant it takes the money; the order only turns `paid`
 * when the webhook lands a beat later. Re-read the order across that gap — offering a pay
 * button to someone who has already paid invites a second payment we would have to refund
 * by hand.
 */
export function usePaymentSettlement({
  orderId,
  token,
  order,
  onOrder,
  returnedFromPayment,
}: Args): SettlementState {
  const [polls, setPolls] = useState(0);
  const pending = returnedFromPayment && order?.status === 'awaiting_payment';

  useEffect(() => {
    if (!pending || !orderId || !token || polls >= MAX_POLLS) return;
    const timer = setTimeout(() => {
      setPolls((n) => n + 1);
      getOrder(orderId, token)
        .then(onOrder)
        .catch(() => undefined);
    }, POLL_MS);
    return () => clearTimeout(timer);
  }, [pending, orderId, token, polls, onOrder]);

  if (!pending) return null;
  return polls >= MAX_POLLS ? 'unconfirmed' : 'settling';
}

export const SETTLEMENT_COPY: Record<'settling' | 'unconfirmed', string> = {
  // The marker only says the buyer came back from Stripe, not that the money arrived: the
  // webhook is the only thing that knows. Say what we know until it lands.
  settling: 'WRÓCILIŚMY Z PŁATNOŚCI - SPRAWDZAMY POTWIERDZENIE U OPERATORA.',
  unconfirmed:
    'POTWIERDZENIE PŁATNOŚCI JESZCZE NIE DOTARŁO. JEŚLI PIENIĄDZE ZOSTAŁY POBRANE, NIE PŁAĆ DRUGI RAZ - POTWIERDZENIE PRZYJDZIE MAILEM. NAPISZ NA KONTAKT@OPERACJATENERYFA.PL, JEŚLI NIC SIĘ NIE ZMIENI.',
};
